/**
 * The price bounds a spot MARKET order carries, and the pre-check that stops an
 * order the risk engine will refuse.
 *
 * Until this existed a spot market order sent `minAmountOut = 0` and
 * `maxQuoteIn = MaxUint256` — "fill me at any price". The units below were
 * probed live on Arc (docs/TRADING-INPUT-STANDARD.md §5a) and are easy to get
 * wrong, because the obvious reading reverts every order:
 *
 *   - `minAmountOut` is in the asset RECEIVED — base for a BUY, quote for a
 *     SELL — and NET of the taker fee, which is charged on the received side.
 *     The gross figure reverts `SlippageTooHigh`.
 *   - `maxQuoteIn` is gross, BUY-only and inclusive. It is not the locked
 *     amount (the lock tracks the book cost either way).
 *   - `calculateOrderAmounts(0, qty, side, 1)` returns the gross book-walked
 *     quote, and REVERTS (`OrderHasNoLiquidity`) when depth < qty.
 */
import { BigNumber, ethers } from "ethers";
import { orderRejected } from "./orderTransactionError";
import { MARKET_ORDER_MESSAGES } from "../../../utils/errors.constants";
import { spotMaxQuoteIn, spotMinAmountOut } from "../../../utils/amount/slippage";
import { ORDERBOOK_QUOTE_ABI } from "./useSpotOrder.helpers";

/** The taker fee, as a fraction feeTaker / feeUnit (2 / 10000 on Arc, Monad, DogeOS). */
export const BALANCE_MANAGER_FEE_ABI = [
  "function feeTaker() view returns (uint256)",
  "function feeUnit() view returns (uint256)",
];

export const SOLVENT_AFTER_WITHDRAWAL_ABI = [
  "function solventAfterWithdrawal(address user, uint256 currencyId, uint256 amount) view returns (bool)",
];

const ORDER_HAS_NO_LIQUIDITY = "0x00e4b96e";

export const SPOT_QUOTE_UNREADABLE_MESSAGE =
  "Couldn't price this order against the book right now, so it was not sent. Try again in a moment.";
export const SPOT_FEE_UNREADABLE_MESSAGE =
  "Couldn't read this market's trading fee, so the order was not sent. Try again in a moment.";

export interface SpotTakerFee {
  feeTaker: bigint;
  feeUnit: bigint;
}

export interface SpotMarketBounds {
  minAmountOut: BigNumber;
  maxQuoteIn: BigNumber;
}

/**
 * The bounds for one market order. Pure: every chain read has already
 * happened, so this is the part worth testing exhaustively.
 */
export const buildSpotMarketBounds = ({
  isBuy,
  qtyRaw,
  grossQuoteRaw,
  slippageBps,
  fee,
}: {
  isBuy: boolean;
  /** Base quantity, in base token units. */
  qtyRaw: bigint;
  /** Gross book-walked quote for that quantity, in quote token units. */
  grossQuoteRaw: bigint;
  slippageBps: number;
  fee: SpotTakerFee;
}): SpotMarketBounds => {
  const received = isBuy ? qtyRaw : grossQuoteRaw;
  const afterSlippage = spotMinAmountOut(received, slippageBps, 0);
  // Floor: asking for a wei more than the fill delivers reverts a good order.
  const netOfFee =
    fee.feeUnit > BigInt(0) && fee.feeTaker >= BigInt(0) && fee.feeTaker < fee.feeUnit
      ? (afterSlippage * (fee.feeUnit - fee.feeTaker)) / fee.feeUnit
      : afterSlippage;
  return {
    minAmountOut: BigNumber.from(netOfFee.toString()),
    // A SELL ignores the cap; uint256.max is the contract's "no cap" (0 would
    // REJECT a buy), so a sell sends that rather than a meaningless number.
    maxQuoteIn: isBuy
      ? BigNumber.from(spotMaxQuoteIn(grossQuoteRaw, slippageBps).toString())
      : ethers.constants.MaxUint256,
  };
};

const revertSelector = (error: any): string | undefined => {
  const data =
    [error?.error?.data?.data, error?.error?.data, error?.data].find(
      (candidate) => typeof candidate === "string" && candidate.startsWith("0x")
    ) ?? undefined;
  return data ? String(data).slice(0, 10).toLowerCase() : undefined;
};

/** Gross quote the book charges (BUY) or pays (SELL) for `qtyRaw`, walked level by level. */
export const quoteSpotMarketOrder = async (
  provider: ethers.providers.Provider,
  orderBook: string,
  qtyRaw: BigNumber,
  side: 0 | 1
): Promise<BigNumber> => {
  try {
    const book = new ethers.Contract(orderBook, ORDERBOOK_QUOTE_ABI, provider);
    const amounts = await book.calculateOrderAmounts(0, qtyRaw, side, 1);
    return BigNumber.from(amounts?.quoteAmount ?? amounts?.[1]);
  } catch (error: any) {
    if (revertSelector(error) === ORDER_HAS_NO_LIQUIDITY) {
      throw orderRejected(MARKET_ORDER_MESSAGES.noBookLiquidity);
    }
    throw orderRejected(SPOT_QUOTE_UNREADABLE_MESSAGE);
  }
};

/** The taker fee from the BalanceManager. Refuses rather than guess a fee. */
export const readSpotTakerFee = async (
  provider: ethers.providers.Provider,
  balanceManager: string
): Promise<SpotTakerFee> => {
  try {
    const bm = new ethers.Contract(balanceManager, BALANCE_MANAGER_FEE_ABI, provider);
    const [feeTaker, feeUnit] = await Promise.all([bm.feeTaker(), bm.feeUnit()]);
    const fee = {
      feeTaker: BigNumber.from(feeTaker).toBigInt(),
      feeUnit: BigNumber.from(feeUnit).toBigInt(),
    };
    if (fee.feeUnit <= BigInt(0) || fee.feeTaker >= fee.feeUnit) throw orderRejected("bad fee");
    return fee;
  } catch {
    throw orderRejected(SPOT_FEE_UNREADABLE_MESSAGE);
  }
};

export type LockSolvency = "solvent" | "insolvent" | "unknown";

export interface LockSolvencyResult {
  verdict: LockSolvency;
  /**
   * The engine's revert payload when it refused to answer at all.
   *
   * Kept because throwing it away is what made the failure unexplainable. On
   * Arc the engine reverts `0x1343a87a(<market>)` -- an unpriceable-market
   * error naming a specific perp -- for EVERY amount, from 1 unit to the whole
   * balance. That was collapsed to "unknown", the order went to the chain, the
   * chain refused with the argument-less `HookRejectedWithdrawal()`, and the UI
   * then told the trader to deposit more money. No deposit of any size could
   * have helped: the refusal was never about quantity.
   */
  revertData?: string;
}

/** Revert bytes, wherever the provider chose to put them. */
const revertDataOf = (value: unknown): string | undefined => {
  const candidate =
    typeof value === "string"
      ? value
      : (value as any)?.data?.data ??
        (value as any)?.error?.data ??
        (value as any)?.data;
  if (typeof candidate !== "string") return undefined;
  // A bare "0x" is an empty return, not an error payload.
  return /^0x[0-9a-f]{8,}$/i.test(candidate) ? candidate.toLowerCase() : undefined;
};

/**
 * A market address from the revert payload, named the way a trader would
 * recognise it ("ETH-PERP"), or `undefined` if it is not one we know.
 *
 * The error's own NAME cannot be recovered locally -- no ABI in this repo or its
 * siblings carries it -- but the payload shape is unambiguous: a 4-byte selector
 * followed by one address word. The address is the useful half.
 */
export const namedMarketInRevert = (
  revertData: string | undefined,
  markets: Record<string, string> | undefined
): string | undefined => {
  if (!revertData || revertData.length < 10 + 64) return undefined;
  const word = revertData.slice(10, 10 + 64);
  if (!/^0{24}[0-9a-f]{40}$/i.test(word)) return undefined;
  const address = `0x${word.slice(24)}`.toLowerCase();
  // The config already keys this map by instrument name ("ETH-PERP"), so the
  // key is the trader-facing name -- no re-derivation needed here.
  return Object.entries(markets || {}).find(
    ([, value]) => String(value).toLowerCase() === address
  )?.[0];
};

const WORD_FALSE = "0x" + "0".repeat(64);
const WORD_TRUE = "0x" + "0".repeat(63) + "1";

/**
 * Would locking `amount` of `token` leave the account solvent? On Arc spot
 * balances and perp margin share one vault, so the risk engine refuses to lock
 * collateral that backs maintenance margin (`HookRejectedWithdrawal`).
 *
 * Only a definite `false` blocks the order. Anything else is "unknown" and the
 * chain decides — the same stance the backend takes for withdrawals. The
 * result is decoded strictly: Arc's risk-engine proxy was observed RETURNING
 * error data (`UnpriceableMarginItem(market)`) as if it were a result, and a
 * lenient bool decode reads those bytes as `true`.
 */
export const checkSpotLockSolvency = async (
  provider: ethers.providers.Provider,
  riskEngine: string | undefined,
  user: string,
  token: string,
  amount: BigNumber
): Promise<LockSolvencyResult> => {
  if (!riskEngine || riskEngine === ethers.constants.AddressZero) {
    return { verdict: "unknown" };
  }
  try {
    const data = new ethers.utils.Interface(SOLVENT_AFTER_WITHDRAWAL_ABI).encodeFunctionData(
      "solventAfterWithdrawal",
      // currencyId is the token address as uint160, as the backend derives it.
      [user, BigNumber.from(token), amount]
    );
    const result = String(await provider.call({ to: riskEngine, data })).toLowerCase();
    if (result === WORD_TRUE) return { verdict: "solvent" };
    if (result === WORD_FALSE) return { verdict: "insolvent" };
    // Anything else is error bytes the proxy handed back as a result, or a
    // shape we do not understand. Still "unknown" -- the chain stays the
    // authority -- but keep the payload so the refusal can be explained.
    return { verdict: "unknown", revertData: revertDataOf(result) };
  } catch (e) {
    // A proper revert lands here, with the payload on the error.
    return { verdict: "unknown", revertData: revertDataOf(e) };
  }
};
